import { Injectable } from "@angular/core";
import { Action, State, StateContext, Store } from "@ngxs/store";
import { produce } from "immer";
import { Observable } from "rxjs";
import { tap } from "rxjs/operators";

import { PermitUserActions } from "../../shared/states/permit-user.action";
import { ReturnCurrentUserDelegationResult } from "../models/return-current-user-delegation-result.model";
import { SubscriptionInvitation } from "../models/subscription-invitation";
import { SubscriptionsService } from "../services/subscriptions.service";
import { SubscriptionsActions } from "./subscriptions.actions";

export interface SubscriptionsStateModel {
    activeAndPendingSubscriptions: SubscriptionInvitation[];
    returningCurrentUserDelegation: boolean;
    returnCurrentUserDelegationSucceeded: boolean | undefined;
    returnCurrentUserDelegationErrorMessage: string | undefined;
}

@Injectable()
@State<SubscriptionsStateModel>({
    name: "subscriptions",
    defaults: {
        activeAndPendingSubscriptions: [],
        returningCurrentUserDelegation: false,
        returnCurrentUserDelegationSucceeded: undefined,
        returnCurrentUserDelegationErrorMessage: undefined,
    },
})
export class SubscriptionsState {
    constructor(private store: Store, private subscriptionsService: SubscriptionsService) {}

    @Action(SubscriptionsActions.GetActiveAndPendingSubscriptions)
    public getActiveAndPendingSubscriptions(
        ctx: StateContext<SubscriptionsStateModel>,
    ): Observable<SubscriptionInvitation[]> {
        return this.subscriptionsService.getActiveAndPendingSubscriptions().pipe(
            tap((activeAndPendingSubscriptions) => {
                ctx.patchState({
                    activeAndPendingSubscriptions: activeAndPendingSubscriptions,
                });
            }),
        );
    }

    @Action(SubscriptionsActions.AnswerInvitation)
    public answerInvitation(
        ctx: StateContext<SubscriptionsStateModel>,
        action: SubscriptionsActions.AnswerInvitation,
    ): Observable<boolean> {
        return this.subscriptionsService.answerInvitation(
            action.identityUserId,
            action.subscriptionInvitation,
            action.signature,
            action.accept,
        ).pipe(tap((response) => {    
            if (response) {
                ctx.dispatch(new PermitUserActions.GetCurrentPerson());
            }
        }));
    }

    @Action(SubscriptionsActions.ReturnCurrentUserDelegation)
    public returnCurrentUserDelegation(
        ctx: StateContext<SubscriptionsStateModel>,
        action: SubscriptionsActions.ReturnCurrentUserDelegation,
    ): Observable<ReturnCurrentUserDelegationResult> | void {
        if (!action.command.returnIssuerDelegation && !action.command.returnSuperintendentDelegation) {
            return;
        }

        ctx.setState(produce((draft: SubscriptionsStateModel) => {
            draft.returningCurrentUserDelegation = true;
            draft.returnCurrentUserDelegationSucceeded = undefined;
            draft.returnCurrentUserDelegationErrorMessage = undefined;
        }));

        return this.subscriptionsService.returnCurrentUserDelegation(action.command).pipe(
            tap((result: ReturnCurrentUserDelegationResult) => {
                ctx.setState(produce((draft: SubscriptionsStateModel) => {
                    draft.returningCurrentUserDelegation = false;
                    draft.returnCurrentUserDelegationSucceeded = result?.success === true;
                    draft.returnCurrentUserDelegationErrorMessage = result?.success === true
                        ? undefined
                        : result?.errorMessage ?? result?.validationResult?.errors?.[0]?.errorMessage;
                }));

                if (result?.success === true) {
                    ctx.dispatch([
                        new PermitUserActions.GetCurrentPerson(),
                        new SubscriptionsActions.GetActiveAndPendingSubscriptions(),
                    ]);
                }
            }),
        );
    }
}
